import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { isSubscriptionActive, isSubscriptionExpiredOrCancelled } from '@/lib/subscription-utils';
import {
  Bell,
  ChevronRight,
  ChevronsLeft,
  ChevronDown,
  Layers,
  PanelLeft,
  Zap,
  IndianRupee,
  ShieldCheck,
  Shield,
  Send,
  BarChart2,
  ScrollText,
  FileText,
  Briefcase,
  Megaphone,
  X,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { SidebarWorkspaceSwitcher } from '@/components/layout/SidebarWorkspaceSwitcher';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/components/AuthProvider';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { ALWAYS_ENABLED_FEATURES } from '@/hooks/useFeatureLimits';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useEntitlementsSettled } from '@/hooks/useEntitlementsSettled';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { useContext } from 'react';
import { cn } from '@/lib/utils';
import { HeaderBusinessSelector } from './HeaderBusinessSelector';
import { PlanCreditsBar } from './PlanCreditsBar';
// Import from menuPermissions.core (NOT lib/menuPermissions) — the latter
// imports this file for its nav arrays, so going through it would be circular.
import { isSectionGranted } from '@/lib/menuPermissions.core';

// Icons Imports
import home_icon from '/assets/new-sidebar-icons/home-overview.svg';
import overview_svg from '/assets/new-sidebar-icons/Dashboard.svg';
import allMessages_svg from '/assets/new-sidebar-icons/All-Messages.svg';
import autoReplies_svg from '/assets/new-sidebar-icons/Auto-Replies-Bots.svg';
import campaigns_svg from '/assets/new-sidebar-icons/Campaigns.svg';
// import adsManager_svg from '/assets/new-sidebar-icons/Ads-Manager.svg';
import appointments_svg from '/assets/new-sidebar-icons/Appointments.svg';
import onlineStore_svg from '/assets/new-sidebar-icons/Online-Store.svg';
import crm_svg from '/assets/new-sidebar-icons/CRM.svg';
import dataStore_svg from '/assets/new-sidebar-icons/Database.svg';
import analytics_svg from '/assets/new-sidebar-icons/Analytics.svg';
import accountSetup from '/assets/new-sidebar-icons/Account-Setup.svg';
import support_svg from '/assets/new-sidebar-icons/Support.svg';
import ai_svg from '/assets/new-sidebar-icons/ai.svg';
import instagram_svg from '/assets/new-sidebar-icons/Instagram.svg';

// Figma-spec sidebar icon set (monochrome, /public/figma-sidebar). Ported from
// the ui_ux FigmaSidebar so the current sidebar uses the same artwork. Assigned
// per FigmaSidebar's verified tab→icon mapping; tabs without a direct figma
// equivalent (Instagram/Petpooja/Support) use the closest match.
const fig_bubbleChat = '/figma-sidebar/bubble-chat.svg';
const fig_campaigns = '/figma-sidebar/campaigns.svg';
const fig_crm = '/figma-sidebar/crm.svg';
const fig_automations = '/figma-sidebar/automations.svg';
const fig_appointments = '/figma-sidebar/appointments.svg';
const fig_shoppingCart = '/figma-sidebar/shopping-cart-02.svg';
const fig_commerce = '/figma-sidebar/commerce.svg';
const fig_dataStore = '/figma-sidebar/data-store.svg';
const fig_reports = '/figma-sidebar/reports-analytics.svg';
const fig_ads = '/figma-sidebar/ads.svg';
const fig_ai = '/figma-sidebar/ai.svg';
const fig_generalSetting = '/figma-sidebar/general-setting.svg';
const fig_menuSquare = '/figma-sidebar/menu-square.svg';
const fig_info = '/figma-sidebar/info.svg';
// Purpose-made globe icon that shipped with the figma set for this exact tab
// (same monochrome #6A7282 as the rest) — unused until Social Media Posts
// became a top-level group on 2026-08-04.
const fig_socialMediaPosts = '/figma-sidebar/social-media-posts.svg';
// Dedicated Instagram icon (uploaded to /public/icons, same monochrome
// #6A7282 style as the figma set) — replace the earlier best-fit.
const icon_instagram = '/icons/instagram.svg';

// Shared cap normalizer — the integration sub-feature values are not reliably
// numbers (VARCHAR overrides, booleans, legacy `{enabled, limit}` rows).
import { hasAnyEntitledIntegration } from '@/lib/integrationEntitlements';

const we_next_logo = '/assets/wenext_logo_dark.png';
const small_we_next_logo = '/assets/loading-icons/we-next-icon.jpg';

// Admin Icons
const dashboard_icon = '/assets/sidebar-icons/superadmin-icons/dashboard-icon.svg';
const dashboard_filled_icon = '/assets/sidebar-icons/superadmin-icons/dashboard-filled-icon.svg';
const clients_icon = '/assets/sidebar-icons/superadmin-icons/clients-icon.svg';
const clients_filled_icon = '/assets/sidebar-icons/superadmin-icons/clients-filled-icon.svg';
const agencies_icon = '/assets/sidebar-icons/superadmin-icons/agencies-icon.svg';
const agencies_filled_icon = '/assets/sidebar-icons/superadmin-icons/agencies-filled-icon.svg';
const subscriptions_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-icon.svg';
const subscriptions_filled_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-filled-icon.svg';
const organizations_icon = '/assets/sidebar-icons/superadmin-icons/agencies-icon.svg';
const organizations_filled_icon = '/assets/sidebar-icons/superadmin-icons/agencies-filled-icon.svg';
const compensations_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-icon.svg';
const compensations_filled_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-filled-icon.svg';
const settings_icon = '/assets/settings.svg';
const settings_filled_icon = '/assets/settings-filled.svg';
const templates_icon = '/assets/templates.svg';
const templates_filled_icon = '/assets/templates-filled.svg';

// Sidebar mirror of the strict RoleRoute guards in App.tsx for role-restricted
// Account-Settings sections. A URL listed here is shown ONLY to the roles in its
// array — even when an admin granted the parent "Account Settings" group via
// custom menu permissions. The RoleRoute guard is the authoritative gate (it
// blocks the page regardless), so without this the sidebar would surface links
// that just redirect — e.g. an Agent seeing "Audit Log" / "Billing" they can't open.
// Keep this in sync with the RoleRoute `allowed` arrays in App.tsx.
export const ROUTE_ROLE_RESTRICTIONS: Record<string, string[]> = {
  '/home/audit-log': ['ADMIN'],
  '/home/billing': ['ADMIN'],
  '/home/integrations': ['ADMIN'],
  '/home/business-accounts': ['ADMIN'],
  '/home/social-accounts': ['ADMIN'],
  '/home/team-management': ['ADMIN'],
  '/home/short-links': ['ADMIN'],
  '/home/plans': ['ADMIN'],
};

export const primaryMenuItems = [
  {
    id: 'channels',
    title: 'Inbox',
    icon: fig_bubbleChat,
    secondaryItems: [
      { title: 'WhatsApp', url: '/home/channels/whatsapp', featureKey: 'inbox' },
      { title: 'Instagram', url: '/home/channels/instagram', featureKey: 'inbox' },
      { title: 'Facebook', url: '/home/channels/facebook', featureKey: 'inbox' },
      { title: 'AI Chats', url: '/home/channels/ai-agents', featureKey: 'inbox' },
    ],
  },
  // 'marketing' (old Campaigns + Message Templates group, pointing at
  // `/home/campaigns` + `/home/templates`) is HIDDEN as of the redesign
  // cutover — the 'new-campaigns' group below already covers both via its
  // own Campaigns/Templates tabs, so keeping both visible was two ways to
  // reach the same functionality. The old routes still exist (nothing here
  // deletes them), just no longer linked from the sidebar. Restore this
  // block verbatim if the cutover needs to be rolled back.
  // {
  //   id: 'marketing',
  //   title: 'Campaigns',
  //   icon: fig_campaigns,
  //   secondaryItems: [
  //     { title: 'Message Templates', url: '/home/templates', featureKey: 'templates' },
  //     { title: 'WhatsApp Campaigns', url: '/home/campaigns', featureKey: 'campaigns' },
  //   ],
  // },
  {
    // Was the TEMPORARY preview entry for the Campaigns/Templates redesign;
    // now the PRIMARY Campaigns nav item post-cutover (the old 'marketing'
    // group above is hidden). Links to `/home/new-campaigns`, which renders
    // both Campaigns and Templates via its own internal tab bar (switching
    // to the Templates tab navigates to `/home/new-templates`).
    id: 'new-campaigns',
    title: 'Campaigns',
    icon: fig_campaigns,
    secondaryItems: [
      { title: 'Campaigns', url: '/home/new-campaigns', featureKey: 'campaigns' },
    ],
  },
  {
    // CRM is a single-node group (like Data Store): the sidebar entry links
    // straight to the board at /home/boards/leads, which hosts the Leads /
    // Deals / Tickets tabs internally. The old Leads / Customers / Notes &
    // Tags list pages are redirected to the board in App.tsx, so they no longer
    // need their own sidebar rows.
    id: 'customers',
    title: 'CRM',
    icon: fig_crm,
    secondaryItems: [
      { title: 'CRM', url: '/home/boards/leads', featureKey: 'contacts' },
    ],
  },
  {
    id: 'automation',
    title: 'Automations',
    icon: fig_automations,
    secondaryItems: [
      { title: 'Automations', url: '/home/automation', featureKey: 'automations' },
      { title: 'Forms', url: '/home/flows', featureKey: 'whatsapp_flows' },
      // Hidden 2026-08-03 — it duplicated a destination that already exists:
      // the Forms workspace renders the very same page inline as its
      // "Form Submissions" tab (`/home/flows?view=submissions` →
      // `<FormDataStore embedded />`). The standalone `/home/form-data` route
      // stays registered in App.tsx so existing bookmarks keep working.
      // { title: 'Form Submissions', url: '/home/form-data', featureKey: 'whatsapp_flows' },
      // { title: 'Google Sheets Data', url: '/home/google-sheets-data' },
    ],
  },
  {
    // Single-node group (same pattern as CRM / Data Store): the row links
    // straight to the workspace, which hosts the All networks / Instagram /
    // Facebook / LinkedIn / YouTube tabs internally, so an accordion would be
    // pure friction. Promoted out of Campaigns on 2026-08-04; moved under
    // Automations on 2026-08-21 (Inbox → Campaigns → CRM → Automations →
    // Social Media).
    //
    // The new group id 'social-media' does NOT strand existing team members:
    // stored `menu_permissions` arrays only name the old 'marketing' id, so
    // LEGACY_SECTION_ID_ALIASES in lib/menuPermissions.core.ts keeps a
    // 'marketing' grant implying this section (both in the sidebar below via
    // isSectionGranted and on the route via AppShell's isMenuRouteAllowed).
    // Labelled "Social Media" (renamed from "Social Media Posts" 2026-08-04).
    // The group title is what actually renders for a single-node group — the
    // secondary item's title is never displayed, but is kept in sync so the two
    // can't disagree later (same as CRM / Data Store).
    id: 'social-media',
    title: 'Social Media',
    icon: fig_socialMediaPosts,
    secondaryItems: [
      { title: 'Social Media', url: '/home/social-media/posts', featureKey: 'social_media_posts' },
    ],
  },
  {
    id: 'instagram',
    title: 'Instagram',
    icon: icon_instagram,
    secondaryItems: [
      { title: 'Instagram Posts', url: '/home/instagram/posts', featureKey: 'instagram_automation' },
      { title: 'Instagram Insights', url: '/home/instagram/insights', featureKey: 'instagram_automation' },
      // { title: 'Automation Flows', url: '/home/instagram/automation', featureKey: 'instagram_automation' },
      { title: 'Instagram Automations', url: '/home/automation/instagram', featureKey: 'instagram_automation' },
      { title: 'Automation Leads', url: '/home/instagram/automation/leads', featureKey: 'instagram_automation' },
      { title: 'Instagram Orders', url: '/home/instagram/orders', featureKey: 'instagram_automation' },
    ],
  },
  // YouTube lives inside the Social Media group (platform tab), not a
  // standalone sidebar group — same pattern as Facebook / LinkedIn / TikTok.
  // Instagram is kept in this array but HIDDEN from the sidebar — see
  // HIDDEN_PRIMARY_MENU_IDS below.
  {
    id: 'scheduling',
    title: 'Appointments',
    icon: fig_appointments,
    secondaryItems: [
      { title: 'Overview', url: '/home/scheduling-overview', featureKey: 'appointments' },
      { title: 'Scheduling', url: '/home/scheduling', featureKey: 'scheduling' },
      { title: 'Meetings', url: '/home/meeting-links', featureKey: 'meeting_links' },
      { title: 'Contacts', url: '/home/scheduling-contacts', featureKey: 'appointments' },
      { title: 'Availability Slots', url: '/home/time-slots', featureKey: 'time_slots' },
      { title: 'Calendar Integrations', url: '/home/scheduling-integrations', featureKey: 'appointments' },
      { title: 'WhatsApp Reminders', url: '/home/whatsapp-reminders', featureKey: 'appointments' },
    ],
  },
  {
    id: 'store',
    title: 'Commerce',
    icon: fig_shoppingCart,
    secondaryItems: [
      { title: 'Overview', url: '/home/commerce/dashboard', featureKey: 'commerce' }, // Using 'commerce' key as dashboard is part of it
      { title: 'Product Catalog', url: '/home/commerce/products', featureKey: 'products' },
      { title: 'Orders', url: '/home/orders', featureKey: 'orders' },
      { title: 'Abandoned Carts', url: '/home/abonded-carts', featureKey: 'abonded_carts' },
      { title: 'Payments', url: '/home/payments', featureKey: 'payments' },
      { title: 'Invoices', url: '/home/invoices', featureKey: 'invoices' },
      // { title: 'Commerce Reports', url: '/home/store-reports', featureKey: 'store_reports' },
    ],
  },
  {
    // Commerce V2 — promoted out of the Commerce group to a top-level module
    // (2026-08-24). It is the tabbed commerce workspace (Products / Orders /
    // Categories / Abandoned carts with an Overview + Settings right rail),
    // a separate surface from the legacy Commerce pages rather than another
    // view of Product Catalog, which is how nesting it under Commerce read.
    //
    // Single-node group (same pattern as Data Store / Social Media): the row
    // links straight to the workspace, whose tabs are internal React state
    // rather than routes, so there is nothing for an accordion to list. The
    // group title is what renders — the secondary item's title is never
    // displayed, but is kept in sync so the two cannot disagree later.
    //
    // 🔴 THE URL IS DELIBERATELY UNCHANGED. `/home/commerce/v2` is already
    // routed (App.tsx) and already on `allowedByRole.ADMIN`, so promoting the
    // row needs no route work — and `activePrimaryId` picks the group owning
    // the LONGEST matching url, so Commerce no longer lights up for it
    // (`/home/commerce/products` does not prefix-match `/home/commerce/v2`).
    //
    // The new group id does NOT strand existing team members: stored
    // `menu_permissions` arrays only name the old 'store' id, so
    // LEGACY_SECTION_ID_ALIASES in lib/menuPermissions.core.ts keeps a 'store'
    // grant implying this section — same treatment 'social-media' got when it
    // was promoted out of 'marketing' on 2026-08-04.
    //
    // Hidden from the sidebar (HIDDEN_PRIMARY_MENU_IDS) as of 2026-08-28 —
    // kept in this array so the route and permission id stay mapped.
    id: 'commerce-v2',
    title: 'Commerce V2',
    // Same glyph as the Commerce group above (`fig_shoppingCart`), on purpose:
    // the two are the same domain, and a different icon read as a different
    // kind of feature. Petpooja keeps `fig_commerce` — it is a separate
    // integration, not a second view of Commerce.
    icon: fig_shoppingCart,
    secondaryItems: [
      { title: 'Commerce V2', url: '/home/commerce/v2', featureKey: 'commerce' },
    ],
  },
  // {
  //   id: 'website-builder',
  //   title: 'Website Builder',
  //   icon: onlineStore_svg,
  //   secondaryItems: [
  //     { title: 'My Sites', url: '/home/website-builder', featureKey: null },
  //   ],
  // },
  {
    id: 'data-store',
    title: 'Data Store',
    icon: fig_dataStore,
    secondaryItems: [
      { title: 'Data Store', url: '/home/data-stores', featureKey: 'data_store' },
    ],
  },
  {
    id: 'ads',
    title: 'Ads',
    icon: fig_ads,
    // Parent gate. When the super-admin disables parent Ads in an override
    // (or a plan has `features.ads = false`), the entire group hides — even
    // if individual child sub-features (meta_ads / linkedin_ads / tiktok_ads)
    // are still > 0 in plan data. Without this, admins had to manually untick
    // each child too; reported 2026-05-14 as "disabled Ads in override but
    // sidebar still shows the group + Meta + LinkedIn". isPrimaryGroupAllowed
    // short-circuits to hidden when a group declares featureKey and that flag
    // is false. No other group currently uses this; safe to add for Ads only.
    featureKey: 'ads',
    secondaryItems: [
      { title: 'Meta Ads', url: '/home/ads', featureKey: 'meta_ads' },
      { title: 'Experiments', url: '/home/ads/experiments', featureKey: 'meta_ads' },
      { title: 'Competitor Ads', url: '/home/competitor-ads', featureKey: null },
      { title: 'LinkedIn Ads', url: '/home/linkedin-ads', featureKey: 'linkedin_ads' },
      { title: 'TikTok Ads', url: '/home/tiktok-ads', featureKey: 'tiktok_ads' },
      { title: 'Google Ads', url: '/home/google-ads', featureKey: 'google_ads' },
    ],
  },
  {
    // Single-node group (same pattern as Data Store / CRM): the row links
    // straight to `/home/analytics`. The accordion only ever showed that one
    // child — Sales & Conversions is not on the role URL allowlist, so it
    // never appeared as a second row. Group title is what renders. The `id`
    // stays `reports` so existing `menu_permissions` grants keep working.
    // Sits under Ads (2026-08-21): Data Store → Ads → Reports → AI Agents.
    //
    // Sales & Conversions stays in this list so `/home/sales-and-conversions`
    // still maps to the `reports` permission id (`buildUrlIdEntries`). It
    // does not render — `isSingleNode` uses the first remaining child.
    id: 'reports',
    title: 'Reports',
    icon: fig_reports,
    secondaryItems: [
      { title: 'Reports', url: '/home/analytics', featureKey: 'analytics' },
      { title: 'Sales & Conversions', url: '/home/sales-and-conversions', featureKey: 'sales_and_conversions' },
    ],
  },
  {
    id: 'ai-tools',
    // Renders as a single top-level row (see `isSingleNode`), so this title is
    // what the user reads — 'AI' was a group header for four sub-entries that no
    // longer expand. The `id` is deliberately UNCHANGED: it is the permission
    // key stored in `users.preferences.menu_permissions`, so renaming the title
    // is safe but renaming the id would revoke access for every existing member.
    title: 'AI Agents',
    icon: fig_ai,
    secondaryItems: [
      // Kept in full even though only the first renders: `buildUrlIdEntries` maps
      // every one of these urls to `ai-tools`, and the role/feature filter picks
      // which one the single row points at (a voice-only user gets ?type=voice).
      { title: 'AI Agents', url: '/home/ai-agents', featureKey: 'ai_generation' },
      { title: 'Live Chat', url: '/home/ai-agents?type=general', featureKey: 'ai_generation' },
      { title: 'WhatsApp AI Agent', url: '/home/ai-agents?type=whatsapp', featureKey: 'ai_generation' },
      { title: 'Voice Agents', url: '/home/ai-agents?type=voice', featureKey: 'ai_calling' },
      // { title: 'Chatbot', url: '/home/chatbot', featureKey: null },
    ],
  },
  {
    id: 'petpooja',
    title: 'Petpooja',
    icon: fig_commerce,
    secondaryItems: [
      { title: 'Overview & Sync', url: '/home/petpooja', featureKey: 'petpooja' },
      { title: 'Order History', url: '/home/petpooja/orders', featureKey: 'petpooja' },
    ],
  },
  {
    id: 'settings',
    title: 'Account Settings',
    icon: fig_generalSetting,
    secondaryItems: [
      { title: 'General Settings', url: '/home/account-settings', featureKey: null },
      // Internal team chat (TeamChat.tsx, /home/team-chat). Nested here rather
      // than as a standalone icon so it naturally inherits this group's
      // per-role filtering — an Agent's `allowedByRole` entry only lists this
      // URL (plus General Settings) inside 'settings', so Team Management /
      // Audit Log / Billing / Integrations / Business Accounts / Plans stay
      // hidden for that role without any extra gating logic.
      { title: 'Team Chat', url: '/home/team-chat', featureKey: null },
      { title: 'Team Management', url: '/home/team-management', featureKey: 'team_management' },
      { title: 'Audit Log', url: '/home/audit-log', featureKey: null },
      { title: 'Integrations', url: '/home/integrations', featureKey: 'integrations' },
      { title: 'Billing', url: '/home/billing', featureKey: null },
      { title: 'Plans & Subscriptions', url: '/home/plans', featureKey: null },
      { title: 'Business Accounts', url: '/home/business-accounts', featureKey: 'business_accounts' },
      // Social Accounts removed 2026-08-06 — it moved into Integrations as the
      // "Social accounts" tab. /home/social-accounts still resolves (it
      // redirects to /home/integrations?tab=social), so ROUTE_ROLE_RESTRICTIONS
      // and the menu-permission path lists deliberately keep their entries.
      // Short Links removed from the sidebar 2026-08-11 — `/home/short-links`
      // still resolves (admin RoleRoute). Keep ROUTE_ROLE_RESTRICTIONS + the
      // menu-permission path lists so a typed URL stays gated the same way.
    ],
  },
  {
    id: 'help',
    title: 'Support',
    icon: fig_info,
    secondaryItems: [
      // Updates + Getting Started moved to the header account menu
      // (SidebarAccountFooter) 2026-08-21. `/home/updates` still resolves
      // (ADMIN/USER/AGENT RoleRoute); `/home/whats-new` redirects there.
      // `/home/how-to-use` still resolves (ADMIN RoleRoute). Keep both out of
      // this list so the Support accordion never re-renders a duplicate of
      // the profile-menu items. Unmapped routes are treated as "never block",
      // and `help` is already in ALWAYS_ALLOWED_SECTION_IDS, so a typed URL
      // stays reachable the same way the modal's "View all" is.
      // { title: 'Updates', url: '/home/updates', featureKey: 'whats_new' },
      // { title: 'Getting Started', url: '/home/how-to-use', featureKey: 'how_to_use' },
      { title: 'FAQs', url: '/home/help-articles', featureKey: 'help_articles' },
      // { title: 'Raise a Ticket', url: '/home/raise-ticket', featureKey: 'raise_ticket' },
      // Contact Support moved onto the Getting Started page as a button
      // 2026-08-21 (`CONTACT_SUPPORT_WHATSAPP_URL` in HowToUse.tsx). The
      // WhatsApp URL is not a route — keep it out of this list so Support
      // never re-renders a duplicate of that page button.
      // { title: 'Contact Support', url: 'https://wa.me/918977232350', featureKey: 'contact_support' },
    ],
  },
];

/**
 * Primary groups that stay in `primaryMenuItems` but are NOT rendered in the
 * sidebar (2026-08-03).
 *
 * `settings` — Account Settings moved to the header account menu
 * (2026-08-21). The group stays in `primaryMenuItems` so `/home/account-settings`,
 * `/home/settings`, `/home/audit-log`, billing, integrations, team-management and
 * business-accounts keep resolving to the `settings` permission id.
 *
 * `instagram` — Instagram became one-source-per-surface: posts/insights are the
 * Instagram tab of the top-level Social Media Posts group (it sat under
 * Campaigns until 2026-08-04), and Instagram automations are
 * the Instagram network tab of Automations (`/home/automation?platform=instagram`,
 * see pages/automation/Flows.tsx). A second top-level Instagram group duplicated
 * both, so it's hidden here.
 *
 * `help` — Support accordion hidden 2026-08-21. Getting Started + Updates live
 * in the header account menu; Contact Support is a button on Getting Started;
 * FAQs (`/home/help-articles`) still resolves. The group stays in
 * `primaryMenuItems` so that URL keeps mapping to the `help` permission id
 * (`ALWAYS_ALLOWED_SECTION_IDS`). Deleting the group would unmap it.
 *
 * `commerce-v2` — hidden 2026-08-28, frontend-only. The workspace and
 * `/home/commerce/v2` route stay; deleting the group would unmap that URL
 * (treated as "never block") and drop the Menu Permissions toggle. Re-show
 * by removing this id, nothing else.
 *
 * Hiding at RENDER time (not by deleting the group) is deliberate — the group's
 * urls still feed `buildUrlIdEntries` in lib/menuPermissions.ts, so
 * `/home/instagram/*` and `/home/automation/instagram` keep resolving to the
 * `instagram` permission id. Removing the entry would drop those routes out of
 * that map, and an unmapped route is treated as "never block", silently opening
 * them to every custom-permission user. It also keeps the Instagram toggle in the
 * Menu Permissions dialog, which is still the gate for those routes.
 */
const HIDDEN_PRIMARY_MENU_IDS: string[] = ['instagram', 'settings', 'help', 'commerce-v2'];

export const standaloneNavItems = [
  // { title: 'Home', url: '/home/main-home', icon: overview_svg, featureKey: null },
  { title: 'Overview', url: '/home', icon: fig_menuSquare, featureKey: null },
  // Inbox is retired — replaced by the Channels section (per-channel routes).
  // /home/inbox still redirects (InboxRedirect) so deep-links keep working.
  // { title: 'Inbox', url: '/home/inbox', icon: allMessages_svg, featureKey: 'inbox' },
  // { title: 'New Inbox', url: '/home/new-inbox', icon: allMessages_svg, featureKey: 'inbox' },
  // { title: 'Social Inbox', url: '/home/social-inbox', icon: allMessages_svg, featureKey: null },
  // Team Chat lives inside the Account Settings group's secondaryItems
  // (below, id: 'settings') instead of as a standalone icon here — see the
  // comment there.
];

const mainNavItems = [
  // { title: 'Home', url: '/main-home', icon: overview_svg, featureKey: null },
  { title: 'Overview', url: '/home', icon: home_icon, featureKey: null },
  { title: 'Inbox', url: '/home/inbox', icon: allMessages_svg, featureKey: 'inbox' },
  { title: 'New Inbox', url: '/home/new-inbox', icon: allMessages_svg, featureKey: 'inbox' },
  // { title: 'Social Inbox', url: '/home/social-inbox', icon: allMessages_svg, featureKey: null },
  { title: 'Contacts', url: '/home/contacts', featureKey: 'contacts' },
  // { title: 'Meta Ads', url: '/home/meta-ads', featureKey: 'meta_ads' },
  // { title: 'Lead Ads Sync', url: '/home/lead-ads-sync', featureKey: 'lead_ads_sync' },
  // { title: 'WhatsApp Ads', url: '/home/whatsapp-ads', featureKey: 'whatsapp_ads' },
  { title: 'Message Templates', url: '/home/templates', featureKey: 'templates' },
  { title: 'WhatsApp Campaigns', url: '/home/campaigns', featureKey: 'campaigns' },
  // { title: 'Instagram Broadcasts', url: '/home/instagram-broadcasts', featureKey: 'instagram_broadcasts' },
  { title: 'Post Scheduling', url: '/home/scheduling-posts', featureKey: 'scheduling_posts' },
  { title: 'Social Media Posts', url: '/home/social-media/posts', featureKey: 'social_media_posts' },
  { title: 'Forms', url: '/home/flows', featureKey: 'whatsapp_flows' },
  { title: 'Form Submissions', url: '/home/form-data', featureKey: 'whatsapp_flows' },
  { title: 'Google Sheets Data', url: '/home/google-sheets-data' },
  { title: 'Customer Boards', url: '/home/boards', featureKey: 'contacts' },
  { title: 'Scheduling', url: '/home/scheduling', featureKey: 'scheduling' },
  { title: 'Availability Slots', url: '/home/time-slots', featureKey: 'time_slots' },
  { title: 'Meetings', url: '/home/meeting-links', featureKey: 'meeting_links' },
  { title: 'Scheduling Contacts', url: '/home/scheduling-contacts', featureKey: 'appointments' },
  { title: 'Calendar Integrations', url: '/home/scheduling-integrations', featureKey: 'integrations' },
  { title: 'WhatsApp Reminders', url: '/home/scheduling/whatsapp-reminders', featureKey: 'whatsapp_reminders' },
  { title: 'Automations', url: '/home/automation', featureKey: 'automations' },
  { title: 'Instagram Posts', url: '/home/instagram/posts', featureKey: 'instagram_automation' },
  { title: 'Instagram Insights', url: '/home/instagram/insights', featureKey: 'instagram_automation' },
  // { title: 'Instagram Automation', url: '/home/instagram/automation', featureKey: 'instagram_automation' },
  { title: 'Instagram Automations', url: '/home/automation/instagram', featureKey: 'instagram_automation' },
  { title: 'Instagram Automation Leads', url: '/home/instagram/automation/leads', featureKey: 'instagram_automation' },
  { title: 'Instagram Orders', url: '/home/instagram/orders', featureKey: 'instagram_automation' },
  // LinkedIn Posts removed 2026-08-04 — see the note in primaryMenuItems above.
  { title: 'Orders', url: '/home/orders', featureKey: 'orders' },
  { title: 'Product Catalog', url: '/home/products', featureKey: 'products' },
  { title: 'Abandoned Carts', url: '/home/abonded-carts', featureKey: 'abonded_carts' },
  { title: 'Payments', url: '/home/payments', featureKey: 'payments' },
  { title: 'Commerce Reports', url: '/home/store-reports', featureKey: 'store_reports' },
  { title: 'Social Accounts', url: '/home/social-accounts', featureKey: 'social_media_posts' },
  { title: 'Notifications', url: '/home/notifications', icon: Bell, featureKey: null },
];

const businessNavItems = [
  { title: 'Business Accounts', url: '/home/business-accounts', featureKey: 'business_accounts' },
  { title: 'Call Logs', url: '/home/call-logs', featureKey: null },
  { title: 'Dashboard', url: '/home/analytics', featureKey: 'analytics' },
  { title: 'Message Reports', url: '/home/message-reports', featureKey: 'message_reports' },
  // { title: 'Campaign Performance', url: '/home/campaign-performance', featureKey: 'campaign_performance' },
  { title: 'Sales Reports', url: '/home/ads-results', featureKey: 'ads_results' },
  { title: 'Sales & Conversions', url: '/home/sales-and-conversions', featureKey: 'sales_and_conversions' },
  { title: 'Commerce', url: '/home/commerce/products', featureKey: 'commerce' },
  { title: 'Team Management', url: '/home/team-management', featureKey: 'team_management' },
  { title: 'General Settings', url: '/home/account-settings', featureKey: null },
  { title: 'Integrations', url: '/home/integrations', featureKey: 'integrations' },
  { title: 'Billing', url: '/home/billing', featureKey: 'billing' },
  // Updates + Getting Started live in the header account menu — see the
  // help group above.
  // { title: 'Updates', url: '/home/updates', featureKey: 'whats_new' },
  // { title: 'Getting Started', url: '/home/how-to-use', featureKey: 'how_to_use' },
  // { title: 'Help Articles', url: '/home/help-articles', featureKey: 'help_articles' },
  // { title: 'Raise a Ticket', url: '/home/raise-ticket', featureKey: 'raise_ticket' },
  // Contact Support lives on Getting Started — see the help group above.
  // { title: 'Contact Support', url: 'https://wa.me/918977232350', featureKey: 'contact_support' },
];

const adminNavItems = [
  { title: 'Dashboard', url: '/admin/dashboard', icon: dashboard_icon, filledIcon: dashboard_filled_icon },
  { title: 'Clients', url: '/admin/clients', icon: clients_icon, filledIcon: clients_filled_icon },
  { title: 'Internal Accounts', url: '/admin/internal-accounts', icon: Shield, filledIcon: Shield },
  { title: 'Agencies', url: '/admin/agencies', icon: agencies_icon, filledIcon: agencies_filled_icon },
  { title: 'Library', url: '/admin/library', icon: templates_icon, filledIcon: templates_filled_icon },
  { title: 'Subscriptions', url: '/admin/subscriptions', icon: subscriptions_icon, filledIcon: subscriptions_filled_icon },
  { title: 'Plans', url: '/admin/subscription-plans', icon: Layers, filledIcon: Layers },
  { title: 'Pricing', url: '/admin/pricing', icon: IndianRupee, filledIcon: IndianRupee },
  { title: 'Revenue Analytics', url: '/admin/revenue-analytics', icon: BarChart2, filledIcon: BarChart2 },
  { title: 'WeNext Billing', url: '/admin/billing/invoices', icon: FileText, filledIcon: FileText },
  // External billing — PhotonX + Maya. Each entry lands on the Dashboard;
  // sub-sections (Clients / Proformas / Tax Invoices) are reached via the tab
  // bar inside ExternalBillingLayout. One row per company avoids the duplicate
  // sidebar / tabs UX users flagged on 2026-05-21.
  { title: 'PhotonX Billing', url: '/admin/photonx', icon: Briefcase, filledIcon: Briefcase },
  { title: 'Maya Billing', url: '/admin/maya', icon: Megaphone, filledIcon: Megaphone },
  { title: 'Admin Users', url: '/admin/users', icon: ShieldCheck, filledIcon: ShieldCheck },
  { title: 'Audit Log', url: '/admin/audit-log', icon: ScrollText, filledIcon: ScrollText },
  { title: 'Communications', url: '/admin/communication-center', icon: Send, filledIcon: Send },
];

const agencyNavItems = [
  { title: 'Dashboard', url: '/home/agency/dashboard', icon: dashboard_icon, filledIcon: dashboard_filled_icon },
  { title: 'Organizations', url: '/home/agency/organizations', icon: organizations_icon, filledIcon: organizations_filled_icon },
  { title: 'Compensations', url: '/home/agency/compensations', icon: compensations_icon, filledIcon: compensations_filled_icon },
  { title: 'General Settings', url: '/home/agency/settings', icon: settings_icon, filledIcon: settings_filled_icon },
];

export const AppSidebar: React.FC = () => {
  const { state, setOpenMobile, isMobile, setOpen, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();
  const collapsed = state === 'collapsed';
  const { user, signOut, profileSynced } = useAuth();
  const businessAccountContext = useContext(BusinessAccountContext);
  const businessAccountId = businessAccountContext?.selectedAccountId || null;
  const activeNavGradient = 'var(--main-color)';
  const { totalUnreadConversations } = useUnreadMessages();

  // Helper to handle external links (like WhatsApp) vs internal navigation
  const handleNavigation = (url: string) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      interceptedNavigate(url);
    }
    if (isMobile) setOpenMobile(false);
  };

  const { data: entitlements, isLoading: entitlementsLoading, isError: entitlementsError } = useOrganizationEntitlements();
  // Settle-detection is shared with ProtectedRoute via useEntitlementsSettled
  // so the rabbit loader and the sidebar skeleton release together. If the
  // sidebar settled before ProtectedRoute, the user would see the rabbit
  // disappear and immediately see a partial sidebar fill in plan features.
  // Vice-versa, releasing ProtectedRoute first while the sidebar still
  // shimmered would show a usable main page next to a permanently-shimmering
  // sidebar. Single source of truth eliminates both.
  const organizationIdForSettle = (user as any)?.organization_id;
  const entitlementsSettled = useEntitlementsSettled(!!organizationIdForSettle);
  // Only render the skeleton on the *initial* fetch.
  // - On error we fall back to whatever defaults the hook returned — the user
  //   sees a usable menu, not a permanent shimmer.
  // - Once settled, background refetches do NOT bring the skeleton back.
  // Also hold the skeleton until the initial /auth/getProfile call has
  // resolved. On warm-start, `menu_permissions` may still change once the
  // backend response lands — releasing the skeleton before then causes the
  // sidebar to re-filter visibly. `profileSynced === false` blocks;
  // `undefined` (older test mocks of useAuth) is treated as not blocking so
  // existing suites don't break.
  const isLoadingEntitlements =
    (!entitlementsError && !entitlementsSettled) || profileSynced === false;
  const baseFeatures = entitlements?.entitlements?.features || {};
  const enabledFeatures = {
    ...baseFeatures,
    // Derive child features from parent plan features
    scheduling: baseFeatures.appointments ?? false,
    time_slots: baseFeatures.appointments ?? false,
    meeting_links: baseFeatures.appointments ?? false,
    whatsapp_reminders: baseFeatures.appointments ?? false,
    orders: baseFeatures.commerce ?? false,
    products: baseFeatures.commerce ?? false,
    abonded_carts: baseFeatures.commerce ?? false,
    payments: baseFeatures.commerce ?? false,
    invoices: baseFeatures.commerce ?? false,
    store_reports: baseFeatures.commerce ?? false,
    // Ads sub-features derived from plan
    // Each Ads sub-feature reads strictly from its own sub_features value.
    // Earlier code OR'd in `baseFeatures.ads === true` which forced both
    // meta_ads and linkedin_ads on whenever the parent Ads flag was enabled
    // — overriding any explicit per-platform override (including `0`). With
    // parent on + only TikTok ticked in an override, Meta + LinkedIn still
    // appeared in the sidebar because of that fallback. TikTok Ads never
    // had the fallback, which is why it behaved consistently. Removed
    // 2026-05-14. The parent flag is irrelevant here — the sidebar group's
    // own visibility is already governed by isPrimaryGroupAllowed below
    // (when every child is hidden the whole group collapses).
    meta_ads: entitlements?.entitlements?.sub_features?.ads?.meta_ads > 0,
    linkedin_ads: entitlements?.entitlements?.sub_features?.ads?.linkedin_ads > 0,
    // Features always enabled (not plan-controlled)
    scheduling_posts: true,
    lead_ads_sync: true,
    whatsapp_ads: true,
    segments: true,
    chat_history: true,
    message_reports: true,
    campaign_performance: true,
    ads_results: true,
    sales_and_conversions: true,
    billing: true,
    integrations: true,
    how_to_use: true,
    whats_new: true,
    help_articles: true,
    raise_ticket: true,
    contact_support: true
  };

  // Shared hook — deduplicated with AppShell/TopNavigation/SubscriptionGuard/etc.
  const { data: currentSubscription, isLoading: isLoadingSubscription, isError: isSubscriptionError } = useCurrentSubscription();

  const hasActiveSubscription = isSubscriptionActive(currentSubscription);

  // Detect expired subscription (both trial and paid) OR no subscription at all.
  // Treat fetch errors as "unknown" — don't strip the user's nav menus down to
  // the expired-plan view based on a transient 401/network blip.
  const isSubscriptionExpired = !isLoadingSubscription && !isSubscriptionError && (
    !currentSubscription ||
    isSubscriptionExpiredOrCancelled(currentSubscription)
  );


  const integrationSubFeatures = entitlements?.entitlements?.sub_features?.integrations;

  /**
   * Does this org have at least ONE usable integration? Drives whether the
   * Integrations nav entry renders at all — an entry that only ever opens a
   * "nothing here" page is worse than no entry.
   *
   * 🔴 The rule lives in `lib/integrationEntitlements`, NOT here, and is shared
   * verbatim with the Integrations page grid. This memo used to own its own copy
   * and inspected `sub_features.integrations` only, so it hid the entry from an
   * org granted a TOP-LEVEL integration (`features.petpooja`) with the parent
   * `integrations` feature off — the entry disappeared for the one integration
   * the admin had actually ticked. Do not re-inline the logic: page-vs-sidebar
   * gating drift is the exact bug class the shared module closes.
   *
   * Reads `baseFeatures` (the raw plan values) and NOT `enabledFeatures`: the
   * latter pins `integrations` to `true` in the "always enabled (not
   * plan-controlled)" block below, so a test against it could never fire.
   *
   * Caps are read via `normalizeSocialCapValue` inside the shared module because
   * the stored value is not reliably a number: per-org overrides are VARCHAR
   * (numeric strings), the minimal-entitlements branch stores a boolean, and
   * legacy rows store `{ enabled, limit }`.
   */
  const hasEnabledIntegrationConnector = React.useMemo(
    () =>
      hasAnyEntitledIntegration({
        // Lenient until entitlements land, and on fetch error — same posture as
        // the subscription check below, which deliberately does not strip the nav
        // on a transient 401/network blip. Also stops the entry flickering out
        // and back. `hasEntitlements: false` is what the shared module treats as
        // "unknown → show".
        hasEntitlements: Boolean(entitlements) && !entitlementsError,
        features: baseFeatures,
        subFeatures: integrationSubFeatures,
        limits: entitlements?.entitlements?.limits,
      }),
    [entitlements, entitlementsError, baseFeatures, integrationSubFeatures]
  );

  const isFeatureEnabled = React.useCallback((featureKey: string | null, url?: string): boolean => {
    // Gated on the URL, NOT on `featureKey === 'integrations'`, because
    // `Calendar Integrations` (/home/scheduling-integrations) reuses that same
    // feature key while having nothing to do with these connectors — keying off
    // the feature would have hidden the scheduling entry too. `url` is optional
    // so any call site that does not pass it keeps its previous behaviour.
    if (url === '/home/integrations') {
      return hasEnabledIntegrationConnector;
    }
    if (!featureKey) return true;
    if (featureKey === 'integrations') {
      return true; // Calendar Integrations et al — unchanged
    }
    if (featureKey === 'billing') {
      return true; // Always show billing
    }
    return enabledFeatures[featureKey] === true;
  }, [enabledFeatures, hasEnabledIntegrationConnector]);

  const handleNavClick = React.useCallback((e: React.MouseEvent) => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isOwner = !!(user as any)?.is_organization_owner;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin || isOwner;
  const currentRole = isAdmin ? 'ADMIN' : (roleRaw === 'AGENT' ? 'AGENT' : (roleRaw === 'RIDER' ? 'RIDER' : 'USER'));

  const showAdminNav = isSuperAdmin;
  const showAgencyNav = isAdmin && isAgency && !isSuperAdmin;
  const showRegularNav = !showAdminNav && !showAgencyNav;

  // Mobile-only plan + AI credits strip (relocated out of the mobile header).
  // Hoisted so the mobile footer container can skip rendering entirely when it
  // would otherwise be an empty bordered strip.
  const showMobilePlanCredits =
    roleRaw === 'ADMIN' && !isSuperAdmin && !isAgency && (isLoadingSubscription || !!currentSubscription);

  const allowedByRole = React.useMemo(() => ({
    ADMIN: [
      '/home', '/home/inbox', '/home/new-inbox', '/home/channels/whatsapp', '/home/channels/instagram', '/home/channels/facebook', '/home/channels/ai-agents', '/home/call-logs',
      '/home/customers', '/home/leads', '/home/segments', '/home/chat-history', '/home/notes-and-tags', '/home/tickets',
      '/home/meta-ads', '/home/lead-ads-sync', '/home/whatsapp-ads', '/home/templates', '/home/campaigns', '/home/new-campaigns', '/home/new-templates',
      '/home/scheduling-posts', '/home/instagram-broadcasts', '/home/social-media/posts', '/home/instagram/posts', '/home/facebook/posts', '/home/linkedin/posts', '/home/flows', '/home/orders', '/home/commerce/products',
      '/home/abonded-carts', '/home/payments', '/home/invoices', '/home/store-reports', '/home/form-data', '/home/scheduling', '/home/time-slots',
      '/home/meeting-links', '/home/scheduling-contacts', '/home/scheduling/whatsapp-reminders', '/home/whatsapp-reminders', '/home/boards/leads', '/home/deals', '/home/deals/stages', '/home/automation', '/home/instagram/automation', '/home/automation/instagram',
      '/home/instagram/inbox', '/home/instagram/insights', '/home/instagram/orders', '/home/instagram/automation/create', '/home/instagram/automation/builder', '/home/instagram/automation/leads', '/home/instagram/automation/logs',
      '/home/analytics', '/home/message-reports', '/home/campaign-performance', '/home/ads-results',
      '/home/business-accounts', '/home/social-accounts', '/home/short-links', '/home/commerce/dashboard', '/home/commerce/v2', '/home/data-stores', '/home/petpooja', '/home/petpooja/orders', '/home/team-management', '/home/audit-log', '/home/account-settings', '/home/settings', '/home/integrations', '/home/scheduling-integrations', '/home/billing', '/home/plans', '/home/updates', '/home/whats-new', '/home/how-to-use', '/home/help-articles', '/home/raise-ticket', 'https://wa.me/918977232350', '/home/ai-agents', '/home/live-chat', '/home/whatsapp-ai-agent', '/home/chatbot', '/home/google-sheets-data', '/home/scheduling-overview', '/home/boards', '/home/social-media/instagram/posts', '/home/linkedin/posts', '/home/ads', '/home/ads/create', '/home/ads/setup', '/home/linkedin-ads', '/home/linkedin-ads/setup', '/home/linkedin-ads/create', '/home/linkedin-ads/analytics', '/home/linkedin-ads/ad-library', '/home/linkedin-ads/events', '/home/linkedin-ads/verified', '/home/tiktok-ads', '/home/google-ads',
      '/home/website-builder',
      '/home/social-media/youtube',
      '/home/social-media/youtube/content',
      '/home/social-media/youtube/comments',
      '/home/social-media/youtube/insights',
      '/home/social-media/youtube/automation',
      '/home/voice-agents',
      '/home/team-chat'
    ],

    USER: ['/home', '/home/inbox', '/home/new-inbox', '/home/channels/whatsapp', '/home/channels/instagram', '/home/channels/facebook', '/home/channels/ai-agents', '/home/contacts', '/home/tickets', '/home/templates', '/home/campaigns', '/home/new-campaigns', '/home/new-templates', '/home/form-data', '/home/boards', '/home/deals', '/home/deals/stages', '/home/account-settings', '/home/settings', '/home/team-management', '/home/integrations', '/home/scheduling-integrations', '/home/billing', '/home/short-links', '/home/competitor-ads', '/home/scheduling', '/home/time-slots', '/home/meeting-links', '/home/scheduling-contacts', '/home/voice-agents', '/home/ai-agents', '/home/team-chat'],

    // /home/team-chat added so Agents can reach the internal Team Chat page —
    // its route already permits AGENT (App.tsx), it just had no nav entry.
    AGENT: ['/home', '/home/inbox', '/home/new-inbox', '/home/channels/whatsapp', '/home/channels/instagram', '/home/channels/facebook', '/home/channels/ai-agents', '/home/tickets', '/home/account-settings', '/home/settings', '/home/voice-agents', '/home/ai-agents', '/home/team-chat'],

    RIDER: ['/home', '/home/orders', '/home/petpooja/orders', '/home/account-settings', '/home/settings'],
  }), []);

  // Custom per-user menu permissions (set by admin in Team Management)
  // null/undefined means use role defaults; [] means no access; [...ids] means only those groups
  const userMenuPermissions: string[] | null | undefined = (user as any)?.menu_permissions;
  const hasCustomPermissions = !isSuperAdmin && Array.isArray(userMenuPermissions);

  // True when the current role may open this URL per the strict RoleRoute guards.
  // URLs not in ROUTE_ROLE_RESTRICTIONS have no role restriction (feature/permission
  // gated only). Prevents the sidebar from showing role-restricted links the user
  // can't actually open — including under custom menu permissions, where the
  // group-level grant must NOT unlock an admin-only sub-section the route still blocks.
  const isRoleAllowedForUrl = React.useCallback((url: string): boolean => {
    const allowed = ROUTE_ROLE_RESTRICTIONS[url];
    return !allowed || allowed.includes(currentRole);
  }, [currentRole]);

  const filterSecondaryItems = React.useCallback((secondaryItems: any[]) => {
    // When subscription is expired, bypass role check — use feature check only
    // (backend now returns expired plan's features via entitlements)
    if (isSubscriptionExpired) {
      return secondaryItems.filter(item => isFeatureEnabled(item.featureKey, item.url));
    }
    return secondaryItems.filter(item =>
      allowedByRole[currentRole].includes(item.url) && isFeatureEnabled(item.featureKey, item.url) && isRoleAllowedForUrl(item.url)
    );
  }, [currentRole, allowedByRole, isFeatureEnabled, isSubscriptionExpired, isRoleAllowedForUrl]);

  const isPrimaryGroupAllowed = React.useCallback((primaryItem: any) => {
    // Group-level parent gate: when the group itself declares a `featureKey`
    // (e.g. the Ads bucket) and that top-level flag is disabled, the entire
    // group hides — no need for every child to also be off. Lets a super-admin
    // disable the Ads override with one click and have the whole sidebar
    // section disappear, instead of having to also untick meta_ads /
    // linkedin_ads / tiktok_ads individually. Only Ads uses this today;
    // other groups omit `featureKey` and behave unchanged.
    if (primaryItem.featureKey && !isFeatureEnabled(primaryItem.featureKey, primaryItem.url)) {
      return false;
    }
    // Even with custom permissions, hide the group if ALL its sub-items have
    // their feature disabled — custom permissions grant menu access but cannot
    // override a feature that the super-admin or plan has turned off.
    const featureFilteredItems = primaryItem.secondaryItems.filter(
      (item: any) => isFeatureEnabled(item.featureKey, item.url)
    );
    if (featureFilteredItems.length === 0) return false;

    if (hasCustomPermissions && !isSubscriptionExpired) {
      // General Settings (personal) + Support stay visible regardless of which
      // sections were granted — unchecking "Account Settings" must not strip a
      // member's own profile/password page. The admin-only sub-pages inside
      // Account Settings are still role-filtered out of the sub-item list.
      if (primaryItem.id === 'settings' || primaryItem.id === 'help') return true;
      // isSectionGranted, not a bare .includes() — a group split out of an older
      // one (see LEGACY_SECTION_ID_ALIASES) must stay visible to members whose
      // stored array only names the pre-split id.
      return isSectionGranted(primaryItem.id, userMenuPermissions!);
    }
    const filteredSubItems = filterSecondaryItems(primaryItem.secondaryItems);
    return filteredSubItems.length > 0;
  }, [hasCustomPermissions, userMenuPermissions, filterSecondaryItems, isSubscriptionExpired, isFeatureEnabled]);

  const canOpenAccountLink = React.useCallback((url: string, featureKey: string | null) => {
    const path = url.split('?')[0];
    return filterSecondaryItems([{ url: path, featureKey }]).length > 0;
  }, [filterSecondaryItems]);

  const isStandaloneItemAllowed = React.useCallback((itemUrl: string, itemId?: string) => {
    // When subscription is expired, allow all standalone items (Overview, Inbox)
    if (isSubscriptionExpired) return true;
    if (hasCustomPermissions) {
      // Map standalone items by a simple ID derived from URL
      const id = itemId || (itemUrl === '/home' ? 'overview' : itemUrl === '/home/inbox' ? 'inbox' : itemUrl);
      return userMenuPermissions!.includes(id);
    }
    return allowedByRole[currentRole].includes(itemUrl);
  }, [hasCustomPermissions, userMenuPermissions, currentRole, allowedByRole, isSubscriptionExpired]);

  // Helper to check if a route or its children are active.
  //
  // Sub-item URLs MAY encode a `?view=` query param to disambiguate
  // workspace sub-tabs that share a single pathname. When a
  // url carries `?view=…`, the active state requires BOTH a pathname
  // match AND a matching `view` query param — otherwise two sub-items
  // pointing at the same pathname would both light up in the sidebar.
  const isRouteActive = React.useCallback((url: string, exact: boolean = false) => {
    const [urlPath, urlSearch] = url.split('?');
    if (urlPath === '/home') {
      return location.pathname === '/home' || location.pathname === '/home/' || location.pathname === '/home/agent';
    }
    // `/home/automation` (WhatsApp Automation) is the parent namespace for the
    // Facebook and Instagram flow builders (`/home/automation/facebook/*`,
    // `/home/automation/instagram*`). Those are OWNED by their own channel
    // groups, so the WhatsApp item must not claim them via prefix match —
    // otherwise it lights up (and opens the wrong accordion) on the FB/IG
    // flow builder. Instagram has its own more-specific sidebar item; Facebook
    // does not, so without this guard the FB builder falls through to here.
    if (urlPath === '/home/automation' && !exact) {
      if (
        location.pathname.startsWith('/home/automation/facebook') ||
        location.pathname.startsWith('/home/automation/instagram')
      ) {
        return false;
      }
    }
    const pathMatches = exact
      ? location.pathname === urlPath
      : location.pathname === urlPath || location.pathname.startsWith(urlPath + '/');
    if (!pathMatches) return false;
    if (urlSearch) {
      const wantedParams = new URLSearchParams(urlSearch);
      const wantedView = wantedParams.get('view');
      if (wantedView) {
        const actualView = new URLSearchParams(location.search).get('view');
        return actualView === wantedView;
      }
      // AI Agents workspace type filter — same pathname, different ?type=
      const wantedType = wantedParams.get('type');
      if (wantedType) {
        const actualType = new URLSearchParams(location.search).get('type');
        return actualType === wantedType;
      }
    }
    // Bare `/home/ai-agents` should NOT stay lit when a typed sibling is active
    if (urlPath === '/home/ai-agents' && !urlSearch) {
      const actualType = new URLSearchParams(location.search).get('type');
      if (actualType && actualType !== 'all') return false;
    }
    return true;
  }, [location.pathname, location.search]);

  // Figure out which primary group owns the CURRENT route. When multiple groups match
  // (e.g. `/home/automation/instagram` matches both Automations's `/home/automation`
  // AND Instagram's `/home/automation/instagram`), the group with the longest
  // matching secondary URL wins — that's the most specific match. Only that one group
  // gets highlighted, so we don't light up two sidebar parents at once.
  const activePrimaryId = React.useMemo(() => {
    let bestId: string | null = null;
    let bestLen = -1;
    for (const item of primaryMenuItems) {
      for (const sub of item.secondaryItems) {
        if (!isRouteActive(sub.url)) continue;
        if (sub.url.length > bestLen) {
          bestLen = sub.url.length;
          bestId = item.id;
        }
      }
    }
    return bestId;
  }, [isRouteActive]);

  // A group is "active" only if it owns the most-specific matching route.
  const isGroupActive = React.useCallback((secondaryItems: any[], groupId?: string) => {
    if (!secondaryItems.some(item => isRouteActive(item.url))) return false;
    if (groupId && activePrimaryId && groupId !== activePrimaryId) return false;
    return true;
  }, [isRouteActive, activePrimaryId]);

  const [openDropdownId, setOpenDropdownId] = React.useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = React.useState<string | null>(null);

  // Sync open accordion with the winning group computed above.
  React.useEffect(() => {
    if (activePrimaryId) {
      setOpenAccordion(activePrimaryId);
    }
  }, [activePrimaryId]);

  const wrapWithTooltip = (navLink: React.ReactElement, title: string, forceHidden: boolean = false) => {
    if (!collapsed || isMobile) {
      return navLink;
    }
    return (
      <Tooltip delayDuration={0} open={forceHidden ? false : undefined}>
        <TooltipTrigger asChild>
          {navLink}
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={forceHidden}
          className="text-white ml-[5px] relative border-0 !overflow-visible"
          style={{
            background: 'var(--main-color)',
            overflow: 'visible',
            zIndex: 60
          }}
        >
          <div
            className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 z-10 pointer-events-none"
            style={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #0EB64F',
            }}
          />
          <span className="relative z-0">{title}</span>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader className="bg-white h-14 sm:h-16 md:h-[62px] border-b border-slate-200 py-0 px-0">
          <div
            className={cn(
              'flex h-full w-full',
              collapsed && !isMobile
                ? 'flex-col items-center justify-center gap-1.5 py-1.5 px-0'
                : 'items-center justify-between pl-4 pr-3'
            )}
          >
            <div
              className="flex items-center cursor-pointer"
              onClick={() => {
                if (showAdminNav) {
                  interceptedNavigate('/admin/dashboard');
                } else if (showAgencyNav) {
                  interceptedNavigate('/home/agency/dashboard');
                } else {
                  interceptedNavigate('/home');
                }
              }}
              aria-label="WeNext home"
            >
              {collapsed && !isMobile ? (
                <img src={small_we_next_logo} alt="WeNext" className="w-7 h-7 rounded-[6px]" />
              ) : (
                <img src={we_next_logo} alt="WeNext Logo" className="w-auto h-[28px]" />
              )}
            </div>

            {/* Mobile: close the Sheet beside the logo. Desktop: collapse
                control while expanded (expand moves to the top bar when
                collapsed — the icon rail is too narrow for a header button). */}
            {isMobile ? (
              <button
                type="button"
                onClick={() => setOpenMobile(false)}
                className="flex items-center justify-center w-8 h-8 rounded-md text-[#6a7282] hover:bg-slate-100 hover:text-[#101828] transition-colors"
                aria-label="Close navigation menu"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            ) : !collapsed ? (
              <button
                type="button"
                onClick={toggleSidebar}
                className="flex items-center justify-center w-5 h-5 text-[#6a7282] hover:opacity-70 transition-opacity"
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </SidebarHeader>

        <SidebarContent className={`flex flex-col h-full gap-0 bg-white scrollbar-hide ${collapsed ? 'px-2 pt-[10px] pb-2' : 'px-5 py-2'}`}>
          {/* Workspace switcher sits ABOVE every nav row so changing BA is
              always one click — not buried in the account footer menu.
              Regular nav only (admin/agency shells have no BAs). Shown even
              while entitlements load; Add workspace stays gated on canOpen. */}
          {showRegularNav && (
            <SidebarWorkspaceSwitcher
              collapsed={collapsed && !isMobile}
              canOpen={canOpenAccountLink}
              onNavigate={handleNavigation}
            />
          )}
          {/* Render a skeleton sidebar while entitlements load so the real nav appears
              fully populated in one shot — never staggered as features resolve in.
              Uses the global `.wenext-shimmer` utility (index.css) so the visual is
              pixel-identical to the cold-load skeleton in ProtectedRoute. */}
          {showRegularNav && isLoadingEntitlements && (
            <div className="flex flex-col gap-2 pt-[10px]" aria-busy="true" aria-label="Loading menu">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 py-1.5 px-[10px]">
                  <div className="wenext-shimmer w-5 h-5 rounded-md flex-shrink-0" />
                  {!collapsed && <div className="wenext-shimmer h-4 rounded-md" style={{ width: `${55 + ((i * 13) % 35)}%` }} />}
                </div>
              ))}
            </div>
          )}
          {showRegularNav && !isLoadingEntitlements && (
            <SidebarGroup className="p-0 list-none pt-[10px]">
              <SidebarMenu className="list-none gap-0">
                {standaloneNavItems
                  .filter(item => isStandaloneItemAllowed(item.url))
                  .map((item) => {
                    const active = isRouteActive(item.url);
                    return (
                      <SidebarMenuItem key={item.title} className="list-none">
                        {wrapWithTooltip(
                          <SidebarMenuButton
                            asChild
                            isActive={active}
                            className="list-none !overflow-visible h-auto py-1.5 pl-[10px] pr-2 rounded-[4px]"
                            style={{
                              backgroundColor: active ? 'var(--secondary-color)' : 'transparent',
                              color: active ? 'white' : 'inherit',
                            }}
                            onMouseEnter={(e) => {
                              if (!active) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!active) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                            onClick={() => {
                              handleNavigation(item.url);
                            }}
                          >
                            <div className={cn("flex items-center gap-2.5 w-full cursor-pointer relative", collapsed && !isMobile && "justify-center gap-0")}>
                              <img
                                src={item.icon}
                                alt={item.title}
                                className={`w-5 h-5 object-contain transition-all ${active ? 'brightness-0 invert' : ''}`}
                              />
                              <span className={cn("truncate flex-1 wn-nav-label", collapsed && !isMobile && "sr-only")}>{item.title}</span>
                              {item.title === 'Inbox' && totalUnreadConversations > 0 && (
                                collapsed && !isMobile ? (
                                  <span className="absolute -top-[2px] -right-[5px] flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                                    {totalUnreadConversations}
                                  </span>
                                ) : (
                                  <span className={`ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs ${active ? 'bg-white text-[var(--secondary-color)]' : 'bg-[var(--secondary-color)] text-white'}`}>
                                    {totalUnreadConversations}
                                  </span>
                                )
                              )}
                            </div>
                          </SidebarMenuButton>,
                          item.title
                        )}
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            </SidebarGroup>
          )}

          {/* Regular Navigation Groups with Accordions */}
          {showRegularNav && !isLoadingEntitlements && primaryMenuItems
            .filter(primaryItem => {
              // Hidden groups keep their permission mapping but never render.
              if (HIDDEN_PRIMARY_MENU_IDS.includes(primaryItem.id)) return false;
              return isPrimaryGroupAllowed(primaryItem);
            })
            .map((primaryItem) => {
              // When custom permissions are active, the admin explicitly granted this group —
              // show all its sub-items instead of filtering by role (which would empty them).
              // Still filter by feature enablement so disabled plan features are hidden.
              const filteredSubItems = (hasCustomPermissions
                ? primaryItem.secondaryItems.filter((item: any) => isFeatureEnabled(item.featureKey, item.url) && isRoleAllowedForUrl(item.url))
                : filterSecondaryItems(primaryItem.secondaryItems)
              ).filter((item: any) =>
                // /home/team-chat duplicates the Messaging tab already inside
                // Team Management (Team Chat + Direct Message) — hide the
                // standalone link for any role that can already open Team
                // Management, so Account Settings never lists both at once.
                !(item.url === '/home/team-chat' && isRoleAllowedForUrl('/home/team-management'))
              );
              const active = isGroupActive(filteredSubItems, primaryItem.id);
              // Single-node groups render as a direct nav button (no accordion,
              // no sub-list) — clicking the row navigates straight to the
              // group's one URL. Used when a group has nothing to disambiguate
              // between, so the expand step is pure friction.
              const isSingleNode =
                primaryItem.id === 'data-store' ||
                primaryItem.id === 'customers' ||
                primaryItem.id === 'social-media' ||
                // TEMPORARY preview entry — has exactly one destination
                // (unlike the real 'marketing' group, which genuinely has
                // two: Templates + Campaigns), so it navigates directly
                // instead of expanding into a redundant single sub-item.
                primaryItem.id === 'new-campaigns' ||
                // Reports: one destination (`/home/analytics`). Sales &
                // Conversions stays in secondaryItems for permission mapping
                // but is not a second sidebar row.
                primaryItem.id === 'reports' ||
                // AI: the four sub-entries were all the SAME page with a
                // different `?type=` filter, and the workspace has a Platforms
                // dropdown for that — so the expand step was pure friction.
                //
                // 🔴 It stays a PRIMARY GROUP with id `ai-tools` rather than
                // moving to `standaloneNavItems`. `standaloneId()` returns the
                // raw url for anything but /home and /home/inbox, so a move
                // would change its permission id from `ai-tools` to
                // `/home/ai-agents` — instantly revoking AI access for every
                // member whose stored `menu_permissions` contains `ai-tools`,
                // and orphaning the four `LEGACY_GOVERNED_PATHS` rows that map
                // to it. Rendering flat changes only the render.
                primaryItem.id === 'ai-tools' ||
                // Commerce V2: one destination. The workspace's Products /
                // Orders / Categories / Carts tabs are internal state, not
                // routes, so an accordion would list a single redundant row.
                primaryItem.id === 'commerce-v2';
              const singleNodeItem = filteredSubItems[0];

              if (isSingleNode && singleNodeItem) {
                // CRM's board hosts the Leads / Deals / Tickets tabs internally
                // (and the old list routes redirect into it), so the single CRM
                // entry should light up for any of those routes — not just
                // /home/boards/leads.
                const singleActive = primaryItem.id === 'customers'
                  ? ['/home/boards/leads', '/home/boards/customers', '/home/tickets'].some((p) => isRouteActive(p))
                  // Sibling report pages still exist as routes (search,
                  // bookmarks, How to Use) even though they are no longer
                  // sidebar rows — keep Reports lit while the user is on one.
                  : primaryItem.id === 'reports'
                    ? [
                        '/home/analytics',
                        '/home/sales-and-conversions',
                        '/home/message-reports',
                        '/home/campaign-performance',
                        '/home/ads-results',
                      ].some((p) => isRouteActive(p))
                  // The legacy AI pages are still ROUTED (D1 keeps them until
                  // §12 S5) and the workspace links out to the agent detail
                  // page, so the row has to light up for those too. Matching
                  // only `/home/ai-agents` would leave the sidebar showing
                  // nothing selected on a page the user reached from it.
                  : primaryItem.id === 'ai-tools'
                    ? [
                        '/home/ai-agents',
                        '/home/whatsapp-ai-agent',
                        '/home/live-chat',
                        '/home/voice-agents',
                        '/home/chatbot',
                      ].some((p) => isRouteActive(p))
                    : isRouteActive(singleNodeItem.url);
                return (
                  <SidebarMenuItem key={primaryItem.id} className="list-none">
                    {wrapWithTooltip(
                      <SidebarMenuButton
                        isActive={singleActive}
                        className={`h-auto py-1.5 pl-[10px] pr-2 rounded-[4px] ${collapsed && !isMobile ? 'w-full justify-center' : ''}`}
                        style={{
                          backgroundColor: singleActive ? 'var(--secondary-color)' : 'transparent',
                          color: singleActive ? 'white' : 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          if (!singleActive) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!singleActive) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={() => handleNavigation(singleNodeItem.url)}
                      >
                        <div className={cn("flex items-center gap-2.5 w-full", collapsed && !isMobile && "justify-center gap-0")}>
                          <img
                            src={primaryItem.icon}
                            alt={primaryItem.title}
                            className={`w-5 h-5 object-contain transition-all ${singleActive ? 'brightness-0 invert' : ''}`}
                          />
                          <span className={`${collapsed && !isMobile ? 'sr-only' : 'wn-nav-label flex-1'}`}>
                            {primaryItem.title}
                          </span>
                        </div>
                      </SidebarMenuButton>,
                      primaryItem.title
                    )}
                  </SidebarMenuItem>
                );
              }

              // IF COLLAPSED: Show as Dropdown
              if (collapsed && !isMobile) {
                return (
                  <SidebarMenuItem key={primaryItem.id}>
                    <HoverCard openDelay={0} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <SidebarMenuButton
                          isActive={active}
                          className="h-10 w-full justify-center rounded-[4px]"
                          style={{
                            backgroundColor: active ? 'var(--secondary-color)' : 'transparent',
                            color: active ? 'white' : 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            if (!active) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!active) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <img
                            src={primaryItem.icon}
                            alt={primaryItem.title}
                            className={`w-5 h-5 object-contain flex-shrink-0 transition-all ${active ? 'brightness-0 invert' : ''}`}
                          />
                          <span className="sr-only">{primaryItem.title}</span>
                        </SidebarMenuButton>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="right"
                        align="start"
                        className="w-56 ml-2 p-2 rounded-xl border shadow-lg bg-white/95 backdrop-blur-sm animate-in fade-in-0 zoom-in-95 slide-in-from-left-2"
                      >
                        <div className="px-2 py-2 text-xs border-b-[1px] font-bold text-[#08090A] uppercase tracking-wider mb-1">
                          {primaryItem.title}
                        </div>
                        {filteredSubItems.map(subItem => {
                          return (
                          <div
                            key={subItem.url}
                            onClick={() => handleNavigation(subItem.url)}
                            className={`
                            cursor-pointer rounded-lg px-3 py-2 text-sm font-medium transition-colors my-0.5
                            focus:bg-[var(--secondary-color)]/10 focus:text-[var(--secondary-color)]
                            flex items-center justify-between gap-2
                            ${isRouteActive(subItem.url)
                                ? 'bg-[var(--secondary-color)]/10 text-[var(--secondary-color)]'
                                : 'text-foreground/80 hover:bg-slate-100 hover:text-foreground'}
                          `}
                          >
                            <span className="truncate">{subItem.title}</span>
                          </div>
                          );
                        })}
                      </HoverCardContent>
                    </HoverCard>
                  </SidebarMenuItem>
                );
              }

              // IF EXPANDED: Show as Collapsible Accordion
              return (
                <Collapsible
                  key={primaryItem.id}
                  asChild
                  open={openAccordion === primaryItem.id}
                  onOpenChange={(isOpen) => setOpenAccordion(isOpen ? primaryItem.id : null)}
                  className="group/collapsible list-none"
                >
                  <SidebarMenuItem className="list-none ">
                    <CollapsibleTrigger asChild >
                      <SidebarMenuButton
                        isActive={active}
                        className="h-auto py-1.5 pl-[10px] pr-2 rounded-[4px]"
                        style={{
                          backgroundColor: active ? 'var(--secondary-color)' : 'transparent',
                          color: active ? 'white' : 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          if (!active) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!active) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div className="flex items-center gap-2.5 w-full min-w-0">
                          <img
                            src={primaryItem.icon}
                            alt={primaryItem.title}
                            className={`w-5 h-5 object-contain transition-all ${active ? 'brightness-0 invert' : ''}`}
                          />
                          <span className="wn-nav-label min-w-0 truncate">{primaryItem.title}</span>
                          {primaryItem.id === 'ads' ? (
                            <span
                              aria-hidden="true"
                              className="shrink-0 rounded-full bg-blue-100 px-1.5 py-[3px] text-[9px] font-bold uppercase leading-none tracking-wider text-blue-700"
                            >
                              Beta
                            </span>
                          ) : null}
                          <ChevronRight className={`ml-auto w-4 h-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 ${active ? 'text-white' : ''}`} />
                        </div>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub className="mr-0 pr-0 pl-3 ml-0 border-l-0 gap-1 py-0 list-none [&>li]:list-none">
                        {filteredSubItems.map(subItem => {
                          // Use exact match if another sibling sub-item starts with this URL
                          const hasChildSibling = filteredSubItems.some(
                            s => s.url !== subItem.url && s.url.startsWith(subItem.url + '/')
                          );
                          const subActive = isRouteActive(subItem.url, hasChildSibling);
                          return (
                            <SidebarMenuSubItem key={subItem.url}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={subActive}
                                onClick={() => {
                                  handleNavigation(subItem.url);
                                }}
                                className="cursor-pointer h-auto py-1.5 w-full translate-x-0
                                border-l-[3px] border-transparent
                                hover:bg-gray-100
                                data-[active=true]:bg-[#e3f7ec]
                                data-[active=true]:border-[var(--secondary-color)]
                                data-[active=true]:text-foreground
                                transition-all rounded-none rounded-r-md pl-2 pr-2"
                              >
                                <div className="flex items-center justify-between gap-2 w-full">
                                  <span className="truncate wn-nav-label">{subItem.title}</span>
                                </div>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            })}

          {/* Admin Navigation */}
          {showAdminNav && (
            <SidebarGroup className="p-0 pt-[10px] list-none">
              <SidebarMenu className="list-none gap-0">
                {adminNavItems.map(item => {
                  const active = isRouteActive(item.url);
                  const IconToRender = typeof item.icon === 'string' ? null : item.icon;

                  return (
                    <SidebarMenuItem key={item.title}>
                      {wrapWithTooltip(
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          className="h-auto py-1.5 pl-[10px] pr-2 rounded-[4px]"
                          style={{
                            backgroundColor: active ? 'var(--secondary-color)' : 'transparent',
                            color: active ? 'white' : 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            if (!active) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!active) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                          onClick={() => handleNavigation(item.url)}
                        >
                          <div className={cn("flex items-center gap-2.5 cursor-pointer w-full", collapsed && !isMobile && "justify-center gap-0")}>
                            {typeof item.icon === 'string' ? (
                              <img
                                src={item.icon}
                                alt={item.title}
                                className={`w-5 h-5 object-contain transition-all ${active ? 'brightness-0 invert' : ''}`}
                              />
                            ) : (
                              IconToRender && <IconToRender className={`w-5 h-5 ${active ? 'text-white' : ''}`} />
                            )}
                            <span className={cn("truncate flex-1 wn-nav-label", collapsed && !isMobile && "sr-only")}>{item.title}</span>
                          </div>
                        </SidebarMenuButton>,
                        item.title
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          )}

          {/* Agency Navigation */}
          {showAgencyNav && (
            <SidebarGroup className="p-0 pt-[10px] list-none">
              <SidebarMenu className="list-none gap-0">
                {agencyNavItems.map(item => {
                  const active = isRouteActive(item.url);

                  return (
                    <SidebarMenuItem key={item.title}>
                      {wrapWithTooltip(
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          className="h-auto py-1.5 pl-[10px] pr-2 rounded-[4px]"
                          style={{
                            backgroundColor: active ? 'var(--secondary-color)' : 'transparent',
                            color: active ? 'white' : 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            if (!active) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!active) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                          onClick={() => handleNavigation(item.url)}
                        >
                          <div className={cn("flex items-center gap-2.5 cursor-pointer w-full", collapsed && !isMobile && "justify-center gap-0")}>
                            <img
                              src={item.icon}
                              alt={item.title}
                              className={`w-5 h-5 object-contain transition-all ${active ? 'brightness-0 invert' : ''}`}
                            />
                            <span className={cn("truncate flex-1 wn-nav-label", collapsed && !isMobile && "sr-only")}>{item.title}</span>
                          </div>
                        </SidebarMenuButton>,
                        item.title
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          )}
        </SidebarContent>
        {/* Mobile Sheet footer: plan + AI credits strip (relocated from the
            mobile header so the top bar stays compact). The account control
            lives in the top bar beside notifications, so it is NOT repeated
            here. Admin/agency shells have no header account menu, so they
            keep HeaderBusinessSelector as a fallback. */}
        {isMobile && (showMobilePlanCredits || !showRegularNav) && (
          <SidebarFooter className="border-t border-slate-100 bg-white px-3 py-3 flex flex-col gap-2">
            {showMobilePlanCredits && (
              <PlanCreditsBar variant="card" className="w-full max-w-full" />
            )}
            {!showRegularNav && (
              <HeaderBusinessSelector
                selectedAccountId={businessAccountContext?.selectedAccountId || ''}
                onAccountChange={(id) => businessAccountContext?.setSelectedAccountId?.(id)}
                alwaysShowDetails
              />
            )}
          </SidebarFooter>
        )}
      </Sidebar>
    </>
  );
};
